import React from 'react'
import PropTypes, { InferProps } from 'prop-types'
import { Card, Text, Flex, Button, Badge, getPaletteColor } from 'pcln-design-system'
import {
  FlightsOutlineGreenOnGreen,
  FlightsOutlineGreenOnLightGreen,
  HotelsOutlineGreenOnGreen,
  HotelsOutlineGreenOnLightGreen,
} from '@pcln/brand'
import styled from 'styled-components'
import FireRudderstackImpressionEvent from './helpers/FireRudderstackImpressionEvent'
import getCguid from './helpers/getCguid'

export const props = {
  handleClick: PropTypes.func,
  isDetailsPage: PropTypes.bool,
  isLightBackground: PropTypes.bool,
  bannerHeading: PropTypes.string,
}

const StyledButton = styled(Button)`
  background-color: ${(props) => getPaletteColor(props.backgroundColor)};
  white-space: nowrap;
`

const FlexBannerContainer = styled(Flex)`
  cursor: pointer;
  &:hover ${StyledButton} {
    background-color: ${(props) => getPaletteColor('background.light')};
    color: ${(props) => getPaletteColor('primary.dark')};
  }
`

const Circle = styled(Card)`
  border-radius: 999px;
  border: 0px;
  height: 56px;
  width: 56px;
`
const MiniCircle = styled(Card)`
  border-radius: 999px;
  border: 0px;
  height: 16px;
  width: 16px;
  position: absolute;
`

const PlusIcon = ({ backgroundColor }) => (
  <MiniCircle boxShadowSize='sm' bg={backgroundColor}>
    <Flex width={1} height='100%' justifyContent='center' alignItems='center'>
      <Text color={getPaletteColor('secondary.dark')} mt='1px'>
        +
      </Text>
    </Flex>
  </MiniCircle>
)

const StyledBadge = styled(Badge)`
  width: fit-content;
`

const BundleAndSavePill = () => (
  <StyledBadge color='secondary.dark' bg='lightHighlight'>
    <Text fontSize='10px'>Bundle & Save</Text>
  </StyledBadge>
)

export const IconsContainer = ({ isLightBackground }) => {
  const FlightsIcon = isLightBackground ? FlightsOutlineGreenOnLightGreen : FlightsOutlineGreenOnGreen
  const HotelsIcon = isLightBackground ? HotelsOutlineGreenOnLightGreen : HotelsOutlineGreenOnGreen
  const backgroundColor = isLightBackground ? 'highlight.light' : 'background.lightest'
  return (
    <Flex flexDirection='row' alignItems='center' justifyContent='center' pr={2} pl={3} py={2}>
      <Circle bg={backgroundColor}>
        <Flex width={1} height='100%' justifyContent='center' alignItems='center'>
          <FlightsIcon size='32px' title='Airplane Icon' />
        </Flex>
      </Circle>

      <PlusIcon backgroundColor={backgroundColor} />

      <Circle bg={backgroundColor}>
        <Flex width={1} height='100%' justifyContent='center' alignItems='center'>
          <HotelsIcon size='32px' title='Hotels Icon' />
        </Flex>
      </Circle>
    </Flex>
  )
}

export type HotelCrossSellComponentProps = InferProps<typeof props>

/** @public */
const HotelCrossSellComponent: React.FC<HotelCrossSellComponentProps> = ({
  handleClick,
  isDetailsPage,
  isLightBackground,
  bannerHeading,
  ...rest
}) => {
  const rudderstack = {
    'data-rudder-event': true,
    'data-sub-type': 'cross_sell',
    'data-cguid': getCguid(),
    'data-child-product': 'STAY',
    'data-parent-product': 'FLY',
    'data-source': isDetailsPage ? 'flights details' : 'flights listing',
    'data-category': 'click',
  }

  return (
    <FlexBannerContainer flexDirection='column' onClick={handleClick} {...rudderstack} {...rest}>
      <FireRudderstackImpressionEvent rudderstack={{ ...rudderstack, 'data-category': 'impression' }}>
        <Card
          boxShadowSize='md'
          borderWidth={0}
          borderRadius='xl'
          bg={isLightBackground ? 'background.lightest' : 'background.tint'}
        >
          <Flex>
            <IconsContainer isLightBackground={isLightBackground} />
            <Flex
              p={3}
              width={1}
              alignItems={['start', null, 'center', 'center']}
              justifyContent='space-between'
              flexDirection={['column', null, 'row', 'row']}
            >
              <Flex flexDirection='column' mr={3}>
                <BundleAndSavePill />
                <Text bold mb={1} fontSize={2} color='text.base'>
                  {bannerHeading}
                </Text>
                <Text color='text.base' fontSize={1}>
                  On average save{' '}
                  <Text.span bold color='success.dark'>
                    $240 per traveler
                  </Text.span>
                </Text>
              </Flex>
              <StyledButton
                variation='subtle'
                color='primary'
                size='medium'
                backgroundColor={isLightBackground ? 'background.base' : 'background.lightest'}
                mt={2}
              >
                Save Now
              </StyledButton>
            </Flex>
          </Flex>
        </Card>
      </FireRudderstackImpressionEvent>
    </FlexBannerContainer>
  )
}

HotelCrossSellComponent.propTypes = props

HotelCrossSellComponent.defaultProps = {
  isLightBackground: false,
  bannerHeading: 'Book a flight & hotel together for extra savings.',
}

export default HotelCrossSellComponent
